import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { database } from '../../Firebase';
import { ref, onValue } from 'firebase/database';

const Namuna17Report = ({ loans, onPrintComplete, reportType = 'loan' }) => {
    const [repayments, setRepayments] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const path = reportType === 'deposit' 
            ? 'Grampanchayat/Panipuravtha/Transactions/DepositReturns' 
            : 'Grampanchayat/Panipuravtha/Transactions/LoanRepayments';
            
        const repaymentsRef = ref(database, path);
        onValue(repaymentsRef, (snapshot) => {
            const data = {};
            snapshot.forEach((child) => {
                const val = child.val();
                const key = reportType === 'deposit' ? val.depositId : val.loanId;
                if (!data[key]) {
                    data[key] = [];
                }
                data[key].push(val);
            });
            setRepayments(data);
            setLoading(false);
        });
    }, [reportType]);

    useEffect(() => {
        if (!loading) {
            setTimeout(() => window.print(), 1000);
        }
    }, [loading]);

    if (loading) return null;

    // Helper to process loan/deposit data
    const processedLoans = loans.map(item => {
        const itemRepayments = repayments[item.id] || [];
        const monthlyRepayments = Array(12).fill(0);
        
        itemRepayments.forEach(rep => {
            const date = new Date(rep.date);
            const month = date.getMonth(); // 0 = Jan, 11 = Dec
            let fyMonthIndex = -1;
            if (month >= 3) {
                 fyMonthIndex = month - 3; // Apr(3) -> 0
            } else {
                 fyMonthIndex = month + 9; // Jan(0) -> 9
            }
            if (fyMonthIndex >= 0 && fyMonthIndex < 12) {
                monthlyRepayments[fyMonthIndex] += parseFloat(rep.amount || 0);
            }
        });

        const totalRepaid = monthlyRepayments.reduce((a, b) => a + b, 0);
        const amount = parseFloat(item.loanAmount || item.depositAmount || 0);
        const closingBalance = amount - totalRepaid;

        return {
            ...item,
            monthlyRepayments,
            totalRepaid,
            closingBalance,
            amount // standardized amount field
        };
    });

    return ReactDOM.createPortal(
        <div className="bg-white min-h-screen text-black fixed inset-0 z-[9999] overflow-auto font-poppins">
             <style>{`
                @media print {
                    @page {
                        size: landscape;
                        margin: 0.5cm;
                    }
                    body * {
                        visibility: hidden;
                    }
                    #namuna17-report, #namuna17-report * {
                        visibility: visible;
                    }
                    #namuna17-report {
                        position: absolute;
                        left: 0;
                        top: 0;
                        width: 100%;
                    }
                    .no-print {
                        display: none !important;
                    }
                }
                .double-row-cell {
                    vertical-align: middle;
                }
            `}</style>

            {onPrintComplete && (
                <button 
                    onClick={onPrintComplete} 
                    className="fixed top-4 right-4 bg-red-600 text-white px-4 py-2 rounded shadow no-print z-50 hover:bg-red-700 font-bold"
                >
                    Close Report
                </button>
            )}

            <div id="namuna17-report" className="p-4 w-full">
                <div className="text-center font-bold mb-4">
                    <h1 className="text-xl">नमुना १७</h1>
                    <h2 className="text-lg">कर्जाऊ दिलेल्या / अनामत ठेवलेल्या रकमांचे नोंदणीपुस्तक</h2>
                    <h3 className="text-base">ग्रामपंचायत इनाम धामणी तालुका मिरज जिल्हा सांगली</h3>
                </div>

                <table className="w-full border-collapse border border-black text-[10px] text-center">
                    <thead>
                        {/* Header Row 1 */}
                        <tr>
                            <th rowSpan="3" className="border border-black p-1 bg-gray-100">महिना आणि <br/> तारीख <br/> (१)</th>
                            <th rowSpan="3" className="border border-black p-1 bg-gray-100">इसमाचे नाव <br/> (२)</th>
                            <th rowSpan="3" className="border border-black p-1 bg-gray-100 text-xs">कर्जाऊ रक्कमेचा <br/> किंवा <br/> अनामत <br/> ठेवलेल्या <br/> रक्कमेचा <br/> तपशील <br/> (३)</th>
                            <th rowSpan="3" className="border border-black p-1 bg-gray-100 text-xs">प्रमाणक <br/> क्रमांक <br/> किंवा <br/> परती <br/> क्रमांक <br/> (४)</th>
                            <th rowSpan="3" className="border border-black p-1 bg-gray-100">रक्कम <br/> (५)</th>
                            <th rowSpan="3" className="border border-black p-1 bg-gray-100">आरंभीची <br/> शिल्लक <br/> (६)</th>
                            <th colSpan="6" className="border border-black p-1 bg-gray-100">नगदी परत दिलेल्या रकमा किंवा तडजोड केलेल्या रकमा</th>
                            <th rowSpan="3" className="border border-black p-1 bg-gray-100 text-xs">परत <br/> केलेल्या <br/> एकूण <br/> रकमा <br/> (१९)</th>
                            <th rowSpan="3" className="border border-black p-1 bg-gray-100 text-xs">फेडीची तारीख <br/> किंवा <br/> तडजोडीच्या <br/> प्रमाणकाचा <br/> नंबर <br/> (२०)</th>
                            <th rowSpan="3" className="border border-black p-1 bg-gray-100 text-xs">वर्षाच्या <br/> शेवटी <br/> उरलेली <br/> शिल्लक <br/> (२१)</th>
                            <th rowSpan="3" className="border border-black p-1 bg-gray-100">शेरा <br/> (२२)</th>
                        </tr>
                        {/* Header Row 2: Months Top */}
                        <tr>
                            <th className="border border-black p-1 bg-gray-50">एप्रिल <br/> (७)</th>
                            <th className="border border-black p-1 bg-gray-50">मे <br/> (८)</th>
                            <th className="border border-black p-1 bg-gray-50">जुन <br/> (९)</th>
                            <th className="border border-black p-1 bg-gray-50">जुलै <br/> (१०)</th>
                            <th className="border border-black p-1 bg-gray-50">ऑगस्ट <br/> (११)</th>
                            <th className="border border-black p-1 bg-gray-50">सप्टेंबर <br/> (१२)</th>
                        </tr>
                        {/* Header Row 3: Months Bottom */}
                        <tr>
                            <th className="border border-black p-1 bg-gray-50">ऑक्टोंबर <br/> (१३)</th>
                            <th className="border border-black p-1 bg-gray-50">नोव्हेंबर <br/> (१४)</th>
                            <th className="border border-black p-1 bg-gray-50">डिसेंबर <br/> (१५)</th>
                            <th className="border border-black p-1 bg-gray-50">जानेवारी <br/> (१६)</th>
                            <th className="border border-black p-1 bg-gray-50">फेब्रुवारी <br/> (१७)</th>
                            <th className="border border-black p-1 bg-gray-50">मार्च <br/> (१८)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {processedLoans.map((loan, idx) => (
                            <React.Fragment key={loan.id || idx}>
                                {/* Data Row Top (Apr-Sep) */}
                                <tr>
                                    <td rowSpan="2" className="border border-black p-1 double-row-cell">{new Date(loan.date).toLocaleDateString('en-GB')}</td>
                                    <td rowSpan="2" className="border border-black p-1 double-row-cell">{loan.personName}</td>
                                    <td rowSpan="2" className="border border-black p-1 double-row-cell">{loan.narration || '-'}</td>
                                    <td rowSpan="2" className="border border-black p-1 double-row-cell">{loan.voucherNo || '-'}</td>
                                    <td rowSpan="2" className="border border-black p-1 double-row-cell">{loan.amount}</td>
                                    <td rowSpan="2" className="border border-black p-1 double-row-cell">{loan.amount}</td>
                                    
                                    {/* Apr-Sep Values */}
                                    {loan.monthlyRepayments.slice(0, 6).map((val, mIdx) => (
                                        <td key={`top-${mIdx}`} className="border border-black p-1 h-8">{val > 0 ? val : '0'}</td>
                                    ))}

                                    <td rowSpan="2" className="border border-black p-1 double-row-cell">{loan.totalRepaid}</td>
                                    <td rowSpan="2" className="border border-black p-1 double-row-cell"></td>
                                    <td rowSpan="2" className="border border-black p-1 double-row-cell">{loan.closingBalance}</td>
                                    <td rowSpan="2" className="border border-black p-1 double-row-cell"></td>
                                </tr>
                                {/* Data Row Bottom (Oct-Mar) */}
                                <tr>
                                    {/* Oct-Mar Values */}
                                    {loan.monthlyRepayments.slice(6, 12).map((val, mIdx) => (
                                        <td key={`bottom-${mIdx}`} className="border border-black p-1 h-8">{val > 0 ? val : '0'}</td>
                                    ))}
                                </tr>
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
                
                <div className="mt-4 text-xs font-bold">
                    टीप : शेरे व दुरुस्त्या ह्यांच्या खरेपणाबद्द्ल त्यावर सरपंचाने सही केली पाहिजे.
                </div>
                <div className="text-right mt-8 font-bold text-sm">
                    ग्रामपंचायत अधिकारी
                </div>
            </div>
        </div>,
        document.body
    );
};

export default Namuna17Report;
